import axios from 'axios';
import { merge } from 'es-toolkit';
import { debounce } from 'es-toolkit';
import Quill from 'quill';
import { getExerciseResultsFromZone } from '../utils/getExerciseResultsFromZone.js';
import { getExerciseResultsBlocks } from '../utils/getExerciseResultsBlocks.js';
import { getExerciseResultsData } from '../utils/getExerciseResultsData.js';
import { getExerciseBlocks } from '../utils/getExerciseBlocks.js';
import { getInitialFormDataModel } from '../utils/getInitialFormDataModel.js';

export default (exerciseData, images, submission, tutor, csrfToken) => ({
  showNametag: true,
  showExercise: false,
  pagination: {
    page: 1,
    nbPages: Object.values(exerciseData.research).length + 1,
    next: function () {
      this.nextPage();
    },
    prev: function () {
      this.prevPage();
    }
  },
  submissionId: submission?.id,
  images: images,
  researchBlocks: null,
  resultsBlocks: null,
  resultsData: [],
  formDataModel: null,
  isFinished: !!submission?.isFinished,
  studentName: submission?.studentName,
  modalIsOpen: false,
  showModalSuccessMessage: false,
  showModalErrorMessage: false,

  debouncedSubmitForm: debounce(function () {
    if (this.isFinished) return;
    this.submitForm();
  }, 250),

  init() {
    this.researchBlocks = getExerciseBlocks(exerciseData.research);
    this.resultsBlocks = getExerciseResultsBlocks(this.researchBlocks);

    const initialFormDataModel = getInitialFormDataModel(this.researchBlocks);
    this.formDataModel = {
      research: submission?.researchData ? merge(initialFormDataModel, submission?.researchData) : initialFormDataModel,
    };

    this.updateResultsData();

    this.initEditor();

    if (this.submissionId && this.studentName) {
      this.showNametag = false;
      this.showExercise = true;
    }

    if (this.isFinished) {
      this.pagination.page = this.pagination.nbPages;
    }

    this.$watch('modalIsOpen', (value) => {
      if (!value) {
        this.showModalSuccessMessage = false;
        this.showModalErrorMessage = false;
      }
    });
  },

  initEditor() {
    document.querySelectorAll('.editor').forEach((el) => {
      const quill = new Quill(el, {
        modules: { toolbar: true },
        theme: 'snow'
      });
      quill.on('text-change', () => {
        this.formDataModel[el.dataset.section][el.dataset.blockId] = quill.getSemanticHTML();
        this.debouncedSubmitForm();
      });
    });
  },

  updateResultsData() {
    this.resultsData = getExerciseResultsData(this.resultsBlocks, this.formDataModel, images);
  },

  dropdownChange(dropdownModel, dropdownImages, imageChoice) {
    if (imageChoice) {
      dropdownModel.images = [];
    } else {
      dropdownModel.images = dropdownImages.map(i => i.id);
    }
    this.debouncedSubmitForm();
  },

  submitForm() {
    this.updateResultsData();
    if (!tutor?.id) return;
    const data = {
      CRAFT_CSRF_TOKEN: csrfToken,
      action: "website/exercise/save",
      exerciseId: exerciseData.id,
      tutorId: tutor.id,
      submissionId: this.submissionId,
      name: this.studentName,
      isFinished: this.isFinished,
      research: this.formDataModel["research"],
    };
    axios.postForm('', data)
      .then((response) => {
        this.submissionId = response.data.submissionId;
      })
      .catch((error) => {
        console.log(error);
      });
  },

  submitExercise() {
    this.isFinished = true;
    this.submitForm();
  },

  prevPage() {
    if (this.pagination.page === 1) {
      return;
    }
    this.pagination.page--;
  },

  nextPage() {
    if (this.pagination.page === this.pagination.nbPages) {
      return;
    }
    this.pagination.page++;
  },

  getPrevPageNotAvailable() {
    return this.pagination.page <= 1;
  },

  getNextPageNotAvailable() {
    return this.pagination.page >= this.pagination.nbPages || (this.pagination.page === this.pagination.nbPages - 1 && !this.getIsResultFinished());
  },

  getIsResultFinished() {
    return this.resultsData.length === this.getResults().length &&
      (this.getDropdownsResults().length <= 0 || this.getResultImages().length > 0);
  },

  getResultsFromZone(zone) {
    return getExerciseResultsFromZone(zone, this.resultsData);
  },

  getResults() {
    return this.resultsData.map(i => i.result).filter(i => !this.checkIfEmpty(i));
  },

  getDropdownsResults() {
    return this.resultsData
      .filter(i => i.type === 'dropdown')
      .map(i => i.result)
      .filter(i => !this.checkIfEmpty(i));
  },

  getResultImages() {
    return this.getDropdownsResults()
      .flatMap(
        d => d.images
          ? Object.values(d.images).flatMap(id => images.find(image => image.id === parseInt(id)))
          : []
      );
  },

  checkImageModel(model, imageId) {
    return Array.isArray(model)
      ? model.map(i => parseInt(i)).includes(imageId)
      : parseInt(model) === imageId;
  },

  checkIfEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object') {
      return Object.values(value)
        .flatMap(v => typeof v === 'object' ? Object.values(v) : v)
        .filter(i => i)
        .length === 0;
    }
    return !value;
  },

  openSendLinkModal() {
    this.modalIsOpen = true;
  },

  sendExerciseLink(url, subject) {
    const email = document.querySelector('input[name="user-email"]').value;
    // Basic email validation


    if (!email) {
      // show error message to user
      return;
    }

    axios.postForm('/actions/website/exercise/send-exercise-link/', {
      exerciseId: exerciseData.id,
      userEmail: email,
      url: url + '?id=' + this.submissionId,
      subject: subject,
      CRAFT_CSRF_TOKEN: csrfToken
    })
      .then(response => {
        this.showModalSuccessMessage = true;
      })
      .catch(error => {
        console.error('Error sending exercise link:', error);
        this.showModalErrorMessage = true;
      });
  }
});
